import {difference, differenceBy, findIndex, ValueIteratee, without} from 'lodash';

import {ErrorWithData} from '../types/customErrors';
import {isDefined} from '../typescript/typeHelpers';

// Used for `myArray.sort(c: Comparator)`
export type Comparator<T> = (a: T, b: T) => number;

export function atIndex<T>(array: ReadonlyArray<T>, index: number): T | undefined {
  return array[index];
}

export function onlyItem<T>(array: ReadonlyArray<T>): T | undefined {
  return array.length === 1 ? array[0] : undefined;
}

export function insert<T>(array: ReadonlyArray<T>, index: number, item: T) {
  return [...array.slice(0, index), item, ...array.slice(index)];
}

export function insertOrReplace<T>(array: ReadonlyArray<T>, index: number, item: T) {
  if (index < array.length) {
    return array.map((value, i) => (i === index ? item : value));
  }

  return insert(array, index, item);
}

export function move<T>(array: ReadonlyArray<T>, newIndex: number, item: T) {
  const index = array.indexOf(item);
  if (index < 0 || index === newIndex) {
    return array;
  }

  return newIndex < index
    ? [...array.slice(0, newIndex), item, ...array.slice(newIndex, index), ...array.slice(index + 1)]
    : [...array.slice(0, index), ...array.slice(index + 1, newIndex + 1), item, ...array.slice(newIndex + 1)];
}

export function moveAt<T>(array: ReadonlyArray<T>, index: number, toIndex: number): Array<T> {
  const insertIndex = index > toIndex ? toIndex : toIndex - 1;
  const item = array[index];

  return insert(removeAt(array, index), insertIndex, item);
}

/**
 * Moves the item at `fromIndex` so that it ends up at `toIndex`.
 *
 * Resolves both ends by position, so unlike `move` it tells duplicate values apart, and unlike `moveAt` — which
 * inserts *before* whatever currently sits at `toIndex` — the item lands on `toIndex` itself. This is the
 * reorder a drag-and-drop hover asks for, where the dragged row takes the hovered row's place.
 */
export function moveToIndex<T>(array: ReadonlyArray<T>, fromIndex: number, toIndex: number): Array<T> {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= array.length ||
    toIndex >= array.length
  ) {
    return [...array];
  }

  return insert(removeAt(array, fromIndex), toIndex, array[fromIndex]);
}

export function insertOrMove<T>(array: ReadonlyArray<T>, newIndex: number, item: T) {
  const index = array.indexOf(item);
  if (index < 0) {
    return insert(array, newIndex, item);
  }

  return move(array, newIndex, item);
}

export function swap<T>(array: ReadonlyArray<T>, indexA: number, indexB: number) {
  if (indexA >= array.length || indexB >= array.length) {
    return array;
  }

  const elementA = array[indexA];
  const elementB = array[indexB];

  return array.map((element, index) => {
    if (index === indexA) {
      return elementB;
    }

    if (index === indexB) {
      return elementA;
    }

    return element;
  });
}

export function splitAt<T>(array: ReadonlyArray<T>, index: number): [Array<T>, Array<T>] {
  const firstArray = array.filter((_value, position) => position < index);
  const secondArray = array.filter((_value, position) => position >= index);
  return [firstArray, secondArray];
}

export function removeAt<T>(array: ReadonlyArray<T>, index: number): ReadonlyArray<T> {
  return [...array.slice(0, index), ...array.slice(index + 1, array.length)];
}

export function intersperse<T>(array: ReadonlyArray<T>, item: T) {
  return intersperseWith(array, () => item);
}

export function intersperseWith<T>(array: ReadonlyArray<T>, makeItem: (first: T, second: T) => T) {
  const result: Array<T> = [];
  let index = 0;

  // Insert the first element.
  if (index < array.length) {
    result.push(array[index++]);
  }

  // Insert the others, with the item in between.
  while (index < array.length) {
    const newItem = makeItem(array[index - 1], array[index]);
    result.push(newItem, array[index++]);
  }

  return result;
}

/**
 * Returns a copy of the array with the item added (if it was not part of the array) or removed (if it was present).
 */
export function arrayToggle<T>(
  array: ReadonlyArray<T>,
  itemToToggle: T,
  compare: (a: T, b: T) => boolean = (a, b) => a === b,
) {
  const existingItem = array.find((item) => compare(itemToToggle, item));

  return isDefined(existingItem) ? without(array, existingItem) : [...array, itemToToggle];
}

/** Adapted from https://stackoverflow.com/a/14130166 */
export function isSubsetArray<T>(entireSet: ReadonlyArray<T>, possibleSubset: ReadonlyArray<T>): boolean {
  return difference(possibleSubset, entireSet).length === 0;
}

export function areArraysShallowEqual<T>(
  a: ReadonlyArray<T>,
  b: ReadonlyArray<T>,
  compareFn?: (a: T, b: T) => boolean,
): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const compare = compareFn ?? Object.is;
  return a.every((_item, i) => compare(a[i], b[i]));
}

/*
 * Index-based, duck-typed sibling of `areArraysShallowEqual`. Unlike that helper
 * (which relies on `Array.prototype.every` and assumes real arrays), this one only
 * touches `.length` and `[i]`, so it tolerates values typed as arrays that are not
 * actually arrays at runtime (e.g. a Redux selector yielding a Set/array-like for a
 * prop typed as an array) instead of throwing "every is not a function".
 *
 * Use this only at boundaries where the runtime shape isn't guaranteed; prefer
 * `areArraysShallowEqual` when the inputs are known to be real arrays.
 */
export function isShallowEqualArrayLike(a: ReadonlyArray<unknown>, b: ReadonlyArray<unknown>): boolean {
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

/*
 * String arrays.
 */

/** Adapted from https://www.geeksforgeeks.org/serialize-deserialize-array-string/ */
export function serializeStringArray(array: ReadonlyArray<string>) {
  return array.map((str) => `${str.length}~${str}`).join('');
}

/** Adapted from https://www.geeksforgeeks.org/serialize-deserialize-array-string/ */
export function deserializeStringArray(string: string) {
  const array = [];
  let lastIndex = 0;
  while (lastIndex < string.length) {
    const delimiterIndex = string.indexOf('~', lastIndex);
    if (delimiterIndex === -1) {
      throw new ErrorWithData('Unable to deserialize string array. (Missing delimiter.)', {
        string,
      });
    }

    const length = Number(string.substring(lastIndex, delimiterIndex));
    if (!Number.isInteger(length) || length < 0 || length > string.length - delimiterIndex - 1) {
      throw new ErrorWithData('Unable to deserialize string array. (Invalid length serialization.)', {
        string,
      });
    }

    array.push(string.substring(delimiterIndex + 1, delimiterIndex + 1 + length));
    lastIndex = delimiterIndex + 1 + length;
  }
  return array;
}

export function countTrueValues(...booleanValues: ReadonlyArray<boolean>): number {
  return booleanValues.filter((value) => value).length;
}

export function isLastItemIndex(items: ReadonlyArray<unknown>, index: number): boolean {
  return index === items.length - 1;
}

export function areUnorderedEqual<TItem>(
  a: ReadonlyArray<TItem>,
  b: ReadonlyArray<TItem>,
  iteratee: ValueIteratee<TItem> = (item: TItem) => item,
): boolean {
  const diffA = differenceBy(a, b, iteratee);
  const diffB = differenceBy(b, a, iteratee);

  return diffA.length === 0 && diffB.length === 0;
}

export function replaceOrAppend<TItem>(
  array: ReadonlyArray<TItem>,
  item: TItem,
  compareFn: (a: TItem, b: TItem) => boolean = (a, b) => a === b,
) {
  const index = findIndex(array, (existingItem) => compareFn(existingItem, item));

  if (index < 0) {
    return [...array, item];
  }

  const copy = [...array];
  copy[index] = item;

  return copy;
}

export function replaceOrAppendArray<TItem>(
  array: ReadonlyArray<TItem>,
  items: ReadonlyArray<TItem>,
  compareFn: (a: TItem, b: TItem) => boolean = (a, b) => a === b,
) {
  return items.reduce((memo, item) => replaceOrAppend(memo, item, compareFn), array);
}

export const emptyArray = Object.freeze([] as const);

export function keyBy<TKey, TValue>(array: ReadonlyArray<TValue>, keyFn: (value: TValue) => TKey) {
  const map = new Map<TKey, TValue>();

  for (const value of array) {
    const key = keyFn(value);
    map.set(key, value);
  }

  return map;
}
